<script setup lang="ts">
import {onMounted, onUnmounted, ref} from 'vue';
import {AUTH_LOG_LABELS, type AuthLogRegel} from 'common';
import {authLog, verwijderAuthLog} from '@/base/callBackend';
import {renderDateTimeLocal} from '@/base/utils';
import DeleteButton from '@/views/icons/DeleteIcon.vue';

// polled: a row can be caused by another user or browser (a login, a forgotten password, a reset link), not only by this one
const VERVERS_MS: number = 5_000;
const URL_REGEX: RegExp  = /(https?:\/\/[^\s]+)/;

const regels = ref<AuthLogRegel[]>([]);
const open   = ref<Set<string>>(new Set());
let timer: ReturnType<typeof setInterval> | undefined;

onMounted(async () => {
    await laad();
    timer = setInterval(laad, VERVERS_MS);
});

onUnmounted(() => {
    clearInterval(timer);
});

async function laad() {
    try {
        regels.value = await authLog();
    } catch (error) {
        // the next poll tries again
    }
}

async function verwijder(regel: AuthLogRegel) {
    await verwijderAuthLog(regel.id);
    regels.value = regels.value.filter(r => r.id !== regel.id);
}

function toggle(regel: AuthLogRegel) {
    if (open.value.has(regel.id)) {
        open.value.delete(regel.id);
    } else {
        open.value.add(regel.id);
    }
}

function isMail(regel: AuthLogRegel): boolean {
    return regel.soort === 'welkom' || regel.soort === 'reset' || regel.soort === 'code';
}

function status(regel: AuthLogRegel): string {
    if (!isMail(regel)) {
        return regel.gelukt ? 'ok' : 'geweigerd';
    }
    if (regel.gelukt) {
        return 'verstuurd';
    }
    return regel.fout ? `mislukt: ${regel.fout}` : 'niet verstuurd (geen mail op deze site)';
}

function statusKleur(regel: AuthLogRegel): string {
    if (regel.gelukt) {
        return 'text-green-700';
    }
    return regel.fout || !isMail(regel) ? 'text-red-700' : 'text-gray-500';
}

// the text is server-generated (see maakMail), so its links are the only thing to pick out; no html rendering
function delen(tekst: string): string[] {
    return tekst.split(URL_REGEX).filter(d => d.length > 0);
}

function isUrl(deel: string): boolean {
    return URL_REGEX.test(deel);
}
</script>

<template>
    <div class="flex flex-col mt-8" data-testid="auth-log">
        <div class="text-xl mb-1">
            Logboek
        </div>
        <div v-if="regels.length === 0" class="text-xs text-gray-500 px-1">
            nog niets gelogd
        </div>
        <div v-else class="flex flex-row items-center w-full text-xs text-gray-500 px-1">
            <div class="w-36 px-3">tijd</div>
            <div class="w-36 px-3">wat</div>
            <div class="w-60 px-3">gebruiker</div>
            <div class="w-36 px-3">ip</div>
            <div class="flex-1 px-3">status</div>
            <div class="icon-button-space"/>
        </div>
        <div v-for="regel in regels" :key="regel.id"
             class="flex flex-col m-0.5 p-1 text-sm bg-gray-100"
             data-testid="auth-log-regel"
        >
            <div class="flex flex-row items-center w-full">
                <div class="flex flex-row items-center flex-1"
                     :class="regel.tekst ? 'cursor-pointer' : ''"
                     @click="regel.tekst && toggle(regel)"
                >
                    <div class="w-36 px-3 whitespace-nowrap">{{ renderDateTimeLocal(new Date(regel.tijd)) }}</div>
                    <div class="w-36 px-3 truncate">{{ AUTH_LOG_LABELS[regel.soort] }}</div>
                    <div class="w-60 px-3 truncate">{{ regel.aan ?? '-' }}</div>
                    <div class="w-36 px-3 truncate">{{ regel.ip ?? '-' }}</div>
                    <div class="flex-1 px-3 truncate" :class="statusKleur(regel)">
                        {{ status(regel) }}
                    </div>
                </div>
                <DeleteButton @click="verwijder(regel)"/>
            </div>
            <div v-if="regel.tekst && open.has(regel.id)"
                 class="mx-3 mt-1 p-2 whitespace-pre-line bg-white border border-gray-300"
                 data-testid="auth-log-tekst"
            >
                <template v-for="(deel, i) in delen(regel.tekst)" :key="i">
                    <a v-if="isUrl(deel)" :href="deel" target="_blank" class="text-blue-700 underline break-all">{{ deel }}</a>
                    <span v-else>{{ deel }}</span>
                </template>
            </div>
        </div>
    </div>
</template>
